import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Send, Camera, CameraOff, Volume2, VolumeX, RefreshCw, AlertTriangle, Mic } from 'lucide-react';
import TutorToby from '@/components/TutorToby';
import VoiceChat from '@/components/VoiceChat';
import { useAudio } from '@/context/AudioContext';
import { toast } from 'sonner';
import useCamera from '@/hooks/useCamera';
import useAttentionTracker from '@/hooks/useAttentionTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TablesModule = () => {
  const navigate = useNavigate();
  const { playSound } = useAudio();
  const [selectedTable, setSelectedTable] = useState(2);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const [tobyMood, setTobyMood] = useState('neutral');
  const [tobyMessage, setTobyMessage] = useState('');
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [attentionWarning, setAttentionWarning] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [voiceChatActive, setVoiceChatActive] = useState(false);
  
  const audioRef = useRef(null);
  const sessionId = useRef(`tables_${Date.now()}`);

  // Camera hook
  const {
    cameras,
    selectedCamera,
    cameraOn,
    videoRef,
    refreshCameras,
    toggleCamera,
    changeCamera
  } = useCamera();

  // Attention tracking hook
  const {
    isDistracted,
    distractionType,
    attentionScore,
    faceDetected,
    resetDistraction
  } = useAttentionTracker(cameraOn, videoRef.current);

  // Play TTS audio with Web Speech fallback
  const speakText = useCallback(async (text, emotion = 'neutral') => {
    if (!voiceEnabled || !text) return;
    
    try {
      setIsSpeaking(true);
      
      const response = await fetch(`${API}/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, tutor: 'toby', emotion })
      });
      
      const data = await response.json();
      
      if (data.audio_url && !data.mock && audioRef.current) {
        audioRef.current.src = data.audio_url;
        await audioRef.current.play();
      } else {
        // Fallback to Web Speech Synthesis
        if ('speechSynthesis' in window) {
          window.speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.lang = 'hi-IN';
          utterance.rate = 0.95;
          utterance.pitch = 0.9;
          utterance.onend = () => setIsSpeaking(false);
          utterance.onerror = () => setIsSpeaking(false);
          window.speechSynthesis.speak(utterance);
          return;
        }
      }
    } catch (error) {
      console.error('TTS error:', error);
      // Fallback to Web Speech on error
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'hi-IN';
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
        return;
      }
    } finally {
      setIsSpeaking(false);
    }
  }, [voiceEnabled]);

  // Handle voice chat response
  const handleVoiceResponse = useCallback((data) => {
    if (data.response) {
      setTobyMessage(data.response);
      setTobyMood(data.emotion === 'excited' ? 'happy' : data.emotion === 'encouraging' ? 'encouraging' : 'neutral');
      
      // Check if response contains answer validation
      const responseText = data.response.toLowerCase();
      if (responseText.includes('sahi') || responseText.includes('correct') || responseText.includes('perfect')) {
        playSound('success');
        setScore(prev => ({ correct: prev.correct + 1, total: prev.total + 1 }));
        toast.success('Sahi jawab! 🎉');
      } else if (responseText.includes('galat') || responseText.includes('wrong') || responseText.includes('try again')) {
        playSound('wrong');
        setScore(prev => ({ ...prev, total: prev.total + 1 }));
      }
    }
  }, [playSound]);

  // Handle distraction detection
  useEffect(() => {
    if (isDistracted && distractionType && !attentionWarning) {
      handleDistraction(distractionType);
    }
  }, [isDistracted, distractionType]);

  const handleDistraction = async (type) => {
    try {
      const response = await fetch(`${API}/interaction/attention?distraction_type=${type}&session_id=${sessionId.current}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      
      setAttentionWarning(data.scolding);
      setTobyMood('thinking');
      playSound('wrong');
      speakText(data.scolding, 'funny');
      
      setTimeout(() => {
        setAttentionWarning(null);
        setTobyMood('neutral');
        resetDistraction();
      }, 4000);
    } catch (error) {
      console.error('Attention API error:', error);
    }
  };

  // Fetch table questions
  const fetchQuestions = useCallback(async (table) => {
    try {
      const response = await fetch(`${API}/learn/tables/${table}`);
      const data = await response.json();
      setQuestions(data.questions);
      setTobyMessage(data.intro);
      setTobyMood('happy');
      setCurrentQuestion(0);
      setScore({ correct: 0, total: 0 });
      speakText(data.intro, 'happy');
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  }, [speakText]);

  useEffect(() => {
    fetchQuestions(selectedTable);
  }, [selectedTable, fetchQuestions]);

  const submitAnswer = async () => {
    if (!userAnswer.trim()) return;
    
    const answer = parseInt(userAnswer, 10);
    
    try {
      const response = await fetch(`${API}/learn/tables/check?table=${selectedTable}&multiplier=${currentQuestion + 1}&answer=${answer}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.correct) {
        playSound('success');
        setTobyMood('happy');
        setScore(prev => ({ correct: prev.correct + 1, total: prev.total + 1 }));
        toast.success(data.message, { duration: 2000 });
      } else {
        playSound('wrong');
        setTobyMood('encouraging');
        setScore(prev => ({ ...prev, total: prev.total + 1 }));
        toast.error(data.message, { duration: 3000 });
      }
      
      setTobyMessage(data.message);
      speakText(data.message, data.correct ? 'happy' : 'encouraging');
      setUserAnswer('');
      
      // Move to next question after delay
      setTimeout(() => {
        if (currentQuestion < questions.length - 1) {
          setCurrentQuestion(prev => prev + 1);
          setTobyMood('neutral');
          askMemoryCheck();
        } else {
          // Completed all questions
          const completeMsg = `Table ${selectedTable} complete! You got ${score.correct + (data.correct ? 1 : 0)} out of ${score.total + 1}! Shabash!`;
          setTobyMessage(completeMsg);
          setTobyMood('excited');
          speakText(completeMsg, 'happy');
        }
      }, 2500);
      
    } catch (error) {
      console.error('Check answer error:', error);
    }
  };

  const askMemoryCheck = async () => {
    // Randomly ask for memory check
    if (Math.random() < 0.3 && currentQuestion > 0) {
      try {
        const chatResponse = await fetch(`${API}/chat/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            message: 'Ask student to repeat the last answer you gave',
            session_id: sessionId.current,
            tutor: 'toby',
            module: 'tables',
            context: { table: selectedTable }
          })
        });
        
        const chatData = await chatResponse.json();
        setTobyMessage(chatData.response);
        setTobyMood('thinking');
      } catch (error) {
        console.error('Memory check error:', error);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      submitAnswer();
    }
  };

  const currentQ = questions[currentQuestion];

  return (
    <div className="min-h-screen relative z-10 pb-8">
      {/* Hidden Audio Element */}
      <audio 
        ref={audioRef} 
        onEnded={() => setIsSpeaking(false)}
        onError={() => setIsSpeaking(false)}
      />
      
      {/* Attention Warning Banner */}
      <AnimatePresence>
        {attentionWarning && (
          <motion.div
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            exit={{ y: -100 }}
            className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-seagreen to-teal text-white py-4 px-6 text-center shadow-lg"
          >
            <div className="flex items-center justify-center gap-3">
              <AlertTriangle className="w-6 h-6 animate-bounce" />
              <span className="font-heading text-lg">{attentionWarning}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Back Button */}
      <Button
        data-testid="back-btn"
        variant="ghost"
        onClick={() => navigate('/')}
        className="back-button bg-white/80 backdrop-blur-sm"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Home
      </Button>

      {/* Voice Toggle */}
      <Button
        data-testid="voice-toggle-btn"
        variant="ghost"
        onClick={() => setVoiceEnabled(!voiceEnabled)}
        className="fixed top-4 right-4 z-50 bg-white/80 backdrop-blur-sm"
      >
        {voiceEnabled ? (
          <Volume2 className="w-5 h-5 text-seagreen" />
        ) : (
          <VolumeX className="w-5 h-5 text-muted-foreground" />
        )}
      </Button>

      {/* Header */}
      <div className="pt-20 pb-8 px-6 text-center">
        <h1 className="text-3xl font-heading text-foreground mb-2">Tables Challenge with Tutor Toby!</h1>
        <p className="text-muted-foreground">Score: {score.correct}/{score.total}</p>
      </div>

      <div className="container mx-auto max-w-6xl px-6">
        {/* Table Selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(num => (
            <Button
              key={num}
              data-testid={`table-${num}-btn`}
              variant={selectedTable === num ? "default" : "outline"}
              onClick={() => setSelectedTable(num)}
              className={`rounded-full w-12 h-12 ${selectedTable === num ? 'bg-seagreen hover:bg-seagreen-dark' : ''}`}
            >
              {num}
            </Button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Question Display */}
          <div className="lg:col-span-2">
            <motion.div
              key={`${selectedTable}-${currentQuestion}`}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="learning-card bg-gradient-to-br from-seagreen to-teal text-white"
            >
              {currentQ && (
                <>
                  <p className="text-lg mb-4 opacity-80">Question {currentQuestion + 1} of {questions.length}</p>
                  <p className="text-4xl font-heading mb-8">{currentQ.display}</p>
                  
                  <div className="flex items-center justify-center gap-4">
                    <Input
                      data-testid="answer-input"
                      type="number"
                      value={userAnswer}
                      onChange={(e) => setUserAnswer(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="?"
                      className="quiz-input bg-white text-seagreen"
                    />
                    <Button
                      data-testid="submit-btn"
                      onClick={submitAnswer}
                      disabled={!userAnswer.trim()}
                      className="btn-playful bg-sunshine text-foreground hover:bg-yellow-400"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Submit
                    </Button>
                  </div>
                </>
              )}
            </motion.div>

            {/* Progress */}
            <div className="mt-4">
              <div className="progress-track">
                <div 
                  className="progress-fill" 
                  style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Tutor Toby + Webcam */}
          <div className="flex flex-col items-center gap-4">
            <div className={`tutor-container toby p-4 rounded-3xl ${attentionWarning ? 'animate-pulse' : ''}`}>
              <TutorToby mood={tobyMood} size="medium" speaking={tobyMood === 'happy'} />
            </div>
            
            <AnimatePresence mode="wait">
              <motion.div
                key={tobyMessage}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="speech-container text-center"
              >
                <p className="text-foreground font-medium text-sm">{tobyMessage || "Ready for tables?"}</p>
              </motion.div>
            </AnimatePresence>

            {/* Webcam */}
            <div className="w-full">
              <div className={`webcam-frame w-full aspect-video bg-chalkboard ${attentionWarning ? 'ring-4 ring-spark' : ''}`}>
                {cameraOn ? (
                  <>
                    <video
                      ref={videoRef}
                      className="w-full h-full object-cover transform scale-x-[-1]"
                      autoPlay
                      playsInline
                      muted
                    />
                    {/* Face Detection Indicator */}
                    <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${faceDetected ? 'bg-seagreen text-white' : 'bg-muted text-muted-foreground'}`}>
                      {faceDetected ? '😊 Face OK' : '🔍 No Face'}
                    </div>
                    {/* Attention Score */}
                    {faceDetected && (
                      <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium bg-white/80">
                        Attention: {Math.round(attentionScore * 100)}%
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center text-white">
                      <CameraOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Camera Off</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Camera Selector */}
              {cameraOn && cameras.length > 1 && (
                <div className="mt-2">
                  <select
                    data-testid="camera-select"
                    value={selectedCamera}
                    onChange={(e) => changeCamera(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-seagreen"
                  >
                    {cameras.map((cam, idx) => (
                      <option key={cam.deviceId} value={cam.deviceId}>
                        {cam.label || `Camera ${idx + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Camera Controls */}
              <div className="flex gap-2 mt-2">
                <Button
                  data-testid="camera-toggle-btn"
                  variant="outline"
                  size="sm"
                  onClick={toggleCamera}
                  className="flex-1"
                >
                  {cameraOn ? <Camera className="w-4 h-4 mr-2" /> : <CameraOff className="w-4 h-4 mr-2" />}
                  {cameraOn ? "Camera On" : "Enable Attention Tracking"}
                </Button>
                
                {cameraOn && (
                  <Button
                    data-testid="refresh-cameras-btn"
                    variant="outline"
                    size="sm"
                    onClick={refreshCameras}
                    title="Refresh camera list"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Voice Chat Section */}
              <div className="mt-4 p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-card">
                <h3 className="text-sm font-heading text-foreground mb-3 flex items-center gap-2">
                  <Mic className="w-4 h-4 text-seagreen" />
                  Voice Chat with Toby
                </h3>
                <VoiceChat
                  tutor="toby"
                  module="tables"
                  context={{ table: selectedTable, question: currentQuestion + 1 }}
                  sessionId={sessionId.current}
                  onResponse={handleVoiceResponse}
                  onListeningChange={setIsListening}
                  onSpeakingChange={setIsSpeaking}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TablesModule;
